<template>
  <section class="summary">
    <div class="panels form-section">
      <div class="panel-left-flex panel-left-gap">
        <h3>Final Summary</h3>
        <div v-if="flights && flights.route.length > 0" class="form-section">
          <h4>Flights</h4>
          <FlightItineraryBook v-if="isSelfBook" :is-edit="false" :flights="flights" />
          <FlightItineraryAssist v-else :flights="flights" />
        </div>
        <div v-if="hotels && hotels.route.length > 0" class="form-section">
          <h4>Hotels</h4>
          <HotelItineraryBook v-if="isSelfBook" :is-edit="false" :hotels="hotels" />
          <HotelItineraryAssist v-else :hotels="hotels" />
        </div>
        <div v-if="cars && cars.route.length > 0" class="form-section">
          <h4>Cars</h4>
          <CarItineraryAssist :cars="cars" />
        </div>
      </div>
      <div v-if="isSelfBook" class="panel-right-flex panel-right-gap">
        <h3>Price Breakdown</h3>
        <div v-if="flights && flights.route.length > 0" class="form-section">
          <h4>Flights</h4>
          <div class="item">
            <div class="line" :class="{ title: flightIsPolicy }">
              <span>{{ flightLabel }}</span>
              {{ flightPrice }}
            </div>
            <div v-if="flightIsPolicy" class="line">
              <span :class="{ green: !flightIsApproval, red: flightIsApproval }">{{ flightIsApproval ? "Pending Approval" : "Approved" }}</span>
              <span :class="{ green: flightInPolicy, red: !flightInPolicy }">{{ flightInPolicy ? "In Policy" : "Out of Policy" }}</span>
            </div>
          </div>
        </div>
        <div v-if="hotels && hotels.route.length > 0" class="form-section">
          <h4>Hotels</h4>
          <div v-for="(leg, index) in hotels.route" :key="index" class="item">
            <div class="line" :class="{ title: leg.ideal.is_policy || leg.ideal.price_total_pay_property > 0 }">
              <span>{{ leg.ideal.refund_description }}</span>
              {{ hotelPriceLabel(leg.ideal.price_currency_symbol, leg.ideal.price_currency, leg.ideal.price_total_pay_now) }}
            </div>
            <div v-if="leg.ideal.price_total_pay_property > 0" class="line" :class="{ title: leg.ideal.is_policy }">
              <span>Pay at Property</span>
              {{ hotelPriceLabel(leg.ideal.price_currency_symbol, leg.ideal.price_currency, leg.ideal.price_total_pay_property) }}
            </div>
            <div v-if="leg.ideal.is_policy" class="line">
              <span :class="{ green: !leg.ideal.is_policy_approval, red: leg.ideal.is_policy_approval }">{{ leg.ideal.is_policy_approval ? "Pending Approval" : "Approved" }}</span>
              <span :class="{ green: leg.ideal.is_policy_within, red: !leg.ideal.is_policy_within }">{{ leg.ideal.is_policy_within ? "In Policy" : "Out of Policy" }}</span>
            </div>
          </div>
        </div>
        <div v-if="cars && cars.route.length > 0" class="form-section">
          <h4>Cars</h4>
          <div class="item">
            <div class="line red">
              <span>Pending Approval</span>
              TBD
            </div>
          </div>
        </div>
        <div class="item">
          <div v-if="totalPrice != 0" class="line">
            <span>Total</span>
            {{ totalPrice }}
          </div>
          <div v-else class="line">No charge until approval received</div>
        </div>
      </div>
      <FAQ
        v-else
        class="panel-right-40-percent"
        :faqs="[
          {
            question: 'What happens next?',
            answer: 'With assisted bookings, a travel expert analyzes your trip and provides a suggested itinerary. You can refine your itinerary until ready to book.',
          },
          {
            question: 'When am I charged?',
            answer: 'With assisted bookings, we charge our service fee upfront. However, we only charge for your bookings if you approve an itinerary to book.',
          },
          {
            question: 'When will I hear from you?',
            answer: 'With assisted bookings, expected wait times are within an hour, although it depends on trip complexity. On weekends, we may take several hours.',
          },
        ]"
      />
    </div>
<div id="navigation">
      <Submit :is-loading="isLoading" :text="isSelfBook ? 'Book Now' : 'Create Request'" @click="submit" />
      <a @click="back">Back</a>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import { formatPrice } from "../utils/money";

import CarItineraryAssist from "./TripNewBookSummaryCarAssist.vue";
import FlightItineraryBook from "./TripNewBookSummaryFlightBook.vue";
import FlightItineraryAssist from "./TripNewBookSummaryFlightAssist.vue";
import HotelItineraryBook from "./TripNewBookSummaryHotelBook.vue";
import HotelItineraryAssist from "./TripNewBookSummaryHotelAssist.vue";
import Submit from "../components/common/FormSubmit.vue";
import FAQ from "../components/common/FaqPanel.vue";

const emit = defineEmits(["back", "submit"]);
const props = defineProps(["isSelfBook", "cars", "flights", "hotels"]);

const isLoading = ref(false);

const flightLastInd = ref(0);
const flightPriceSelected = ref({});
if (props.flights && props.flights.route && props.flights.route.length > 0) {
  flightLastInd.value = props.flights.route.length - 1;
  flightPriceSelected.value = props.flights.route[flightLastInd.value].ideal.price_selected;
}
const flightLabel = computed(() => {
  if (flightPriceSelected.value.amount && flightPriceSelected.value.amount > 0) {
    return flightPriceSelected.value.fare;
  } else {
    return props.flights.cabin;
  }
});
const flightInPolicy = computed(() => {
  if (flightPriceSelected.value.amount && flightPriceSelected.value.amount > 0) {
    return flightPriceSelected.value.is_policy_within;
  } else {
    return false;
  }
});
const flightIsPolicy = computed(() => {
  if (flightPriceSelected.value.amount && flightPriceSelected.value.amount > 0) {
    return flightPriceSelected.value.is_policy;
  } else {
    return false;
  }
});
const flightIsApproval = computed(() => {
  if (flightPriceSelected.value.amount && flightPriceSelected.value.amount > 0) {
    return flightPriceSelected.value.is_policy_approval;
  } else {
    return false;
  }
});
const flightPrice = computed(() => {
  return flightPriceLabel(flightPriceSelected.value);
});
// p.amount is canonical USD; formatPrice consults the org currency captured at search time.
// (Returns "TBD" for amount=0; the legacy "TBA" string is collapsed into the shared helper.)
const flightPriceLabel = p => formatPrice(p.amount);

const hotelPriceLabel = (sym, curr, p) => {
  var prefix = sym ? sym : curr;
  return (
    prefix +
    parseFloat(parseFloat(p).toFixed(2)).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      useGrouping: true,
    })
  );
};

const totalPrice = computed(() => {
  var flightTotalUsd = 0;
  var hotelTotal = 0;
  var hotelSym = null;
  var hotelCurr = null;

  if (props.flights && props.flights.route.length > 0 && flightPriceSelected.value.amount > 0) {
    flightTotalUsd = flightPriceSelected.value.amount; // canonical USD
  }

  if (props.hotels && props.hotels.route.length > 0) {
    props.hotels.route.forEach(leg => {
      hotelSym = leg.ideal.price_currency_symbol;
      hotelCurr = leg.ideal.price_currency;
      hotelTotal += leg.ideal.price_total_pay_now;
    });
  }

  // Flight-only: format via formatPrice (org currency display from USD).
  // Hotel-only: hotels carry their own per-property currency; render as before.
  // Mixed: keep the existing pre-existing rendering (sums values across currencies),
  // documented separately — not introduced by this change.
  if (flightTotalUsd > 0 && hotelTotal === 0) {
    return formatPrice(flightTotalUsd);
  }
  if (flightTotalUsd === 0 && hotelTotal > 0) {
    return hotelPriceLabel(hotelSym, hotelCurr, hotelTotal);
  }
  if (flightTotalUsd > 0 && hotelTotal > 0) {
    return hotelPriceLabel(hotelSym, hotelCurr, flightTotalUsd + hotelTotal);
  }
  return 0;
});

const back = () => {
  emit("back", false);
};

const submit = () => {
  isLoading.value = true;
  emit("submit");
};

defineExpose({ resetLoading: () => { isLoading.value = false; } });
</script>

<style lang="less">
</style>
